// src/components/admin/Header.js
import React, { useState, useRef, useEffect } from 'react';
import { 
  Search, Bell, Settings, LogOut, Menu, User, Shield, 
  ChevronDown, Command, X, FileText, LayoutDashboard,
  Users, FileSignature, Info, Mail, HelpCircle
} from 'lucide-react';

export default function Header({ user, onLogout, onMenuClick, activeSection = 'dashboard' }) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const notificationsRef = useRef(null);
  const profileRef = useRef(null);

  // Cerrar dropdowns al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Atajo de teclado para búsqueda (Ctrl+K / Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        document.getElementById('global-search')?.focus();
      }
      if (e.key === 'Escape') {
        setShowNotifications(false);
        setShowProfileMenu(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Mapeo de secciones para breadcrumb
  const sectionLabels = {
    dashboard: { label: 'Dashboard', icon: LayoutDashboard },
    documents: { label: 'Documentos', icon: FileText },
    templates: { label: 'Plantillas', icon: FileSignature },
    clients: { label: 'Clientes', icon: Users },
    about: { label: 'Nosotros', icon: Info },
    policies: { label: 'Políticas', icon: FileText },
    terms: { label: 'Términos', icon: FileText },
    contact: { label: 'Contacto', icon: Mail },
    settings: { label: 'Ajustes', icon: Settings },
    users: { label: 'Usuarios', icon: Users },
    help: { label: 'Ayuda', icon: HelpCircle },
  };

  const currentSection = sectionLabels[activeSection] || sectionLabels.dashboard;
  const Icon = currentSection.icon;

  // Notificaciones de ejemplo (puedes conectarlas a tu backend)
  const notifications = [
    {
      id: 1,
      title: 'Documento firmado',
      message: 'El contrato de Juan Pérez ha sido firmado exitosamente',
      time: 'Hace 5 minutos',
      read: false,
      type: 'success'
    },
    {
      id: 2,
      title: 'Nuevo usuario registrado',
      message: 'María García se ha registrado en la plataforma',
      time: 'Hace 1 hora',
      read: false,
      type: 'info'
    },
    {
      id: 3,
      title: 'Link expirado',
      message: 'El link de firma del documento "Contrato 2024" ha expirado',
      time: 'Hace 3 horas',
      read: true,
      type: 'warning'
    },
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>;
      case 'warning': return <div className="w-2 h-2 bg-amber-500 rounded-full"></div>;
      case 'info': return <div className="w-2 h-2 bg-blue-500 rounded-full"></div>;
      default: return <div className="w-2 h-2 bg-slate-400 rounded-full"></div>;
    }
  };

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
      <div className="flex items-center justify-between px-4 lg:px-6 h-16">
        {/* Izquierda: Menú móvil + Breadcrumb */}
        <div className="flex items-center gap-4 flex-1">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Abrir menú"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Breadcrumb de sección actual */}
          <div className="hidden sm:flex items-center gap-2 text-sm">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-200">
              <Icon className="w-4 h-4 text-blue-600" />
              <span className="font-semibold text-slate-900">{currentSection.label}</span>
            </div>
          </div>

          {/* Barra de búsqueda 
          <div className="hidden md:flex items-center flex-1 max-w-xl ml-4">
            <div className={`
              relative w-full transition-all duration-200
              ${searchFocused ? 'scale-[1.02]' : ''}
            `}>
              <Search className={`
                absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors
                ${searchFocused ? 'text-blue-600' : 'text-slate-400'}
              `} />
              <input
                id="global-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Buscar documentos, clientes..."
                className="w-full pl-10 pr-16 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm 
                  focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white focus:border-blue-300 
                  transition-all"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                <kbd className="hidden lg:inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-slate-500 bg-white border border-slate-200 rounded">
                  <Command className="w-3 h-3" />
                  K
                </kbd>
              </div>
            </div>
          </div>*/}


        </div>

        {/* Derecha: Acciones */}
        <div className="flex items-center gap-2 lg:gap-3">


          {/* Notificaciones
          <div className="relative" ref={notificationsRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Notificaciones"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full border-2 border-white">
                  {unreadCount}
                </span>
              )}
            </button>

    
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <h3 className="text-sm font-semibold text-slate-900">Notificaciones</h3>
                  <button className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                    Marcar todas como leídas
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${
                        !notification.read ? 'bg-blue-50/30' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1.5 flex-shrink-0">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {notification.title}
                          </p>
                          <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-1">
                            {notification.time}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
                  <button className="w-full text-center text-xs font-medium text-blue-600 hover:text-blue-700 py-1">
                    Ver todas las notificaciones
                  </button>
                </div>
              </div>
            )}
          </div>
 */}
          {/* Configuración */}
          <button
            className="hidden sm:flex p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Configuración"
          >
            <Settings className="w-5 h-5" />
          </button>

          {/* Divider */}
          <div className="hidden sm:block w-px h-8 bg-slate-200"></div>

          {/* Perfil de usuario con dropdown */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-3 pl-2 pr-1 py-1 hover:bg-slate-50 rounded-lg transition-colors"
              aria-label="Menú de perfil"
            >
              <div className="hidden sm:block text-right">
                <p className="text-sm font-semibold text-slate-900 leading-tight">
                  {user?.username || 'Usuario'}
                </p>

                {/* <p className="text-[10px] font-medium text-amber-600 uppercase tracking-wider flex items-center gap-1 justify-end">
                  <Shield className="w-3 h-3" />
                  {user?.role === 'admin' ? 'Administrador' : 'Usuario'}
                </p> */}

              </div>
              <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm">
                <User className="w-4 h-4 text-white" />
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${showProfileMenu ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown de perfil */}
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                {/* Info del usuario */}
                <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-blue-50">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {user?.username || 'Usuario'}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {user?.email || 'usuario@ejemplo.com'}
                      </p>
{/*                       <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-semibold">
                        <Shield className="w-3 h-3" />
                        {user?.role === 'admin' ? 'Administrador' : 'Usuario'}
                      </span> */}
                    </div>
                  </div>
                </div>

                {/* Opciones del menú */}
                <div className="py-2">
                  <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                    <User className="w-4 h-4 text-slate-500" />
                    Mi Perfil
                  </button>
                 

<button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                    <Settings className="w-4 h-4 text-slate-500" />
                    Configuración
                  </button>

                  {/* ✅ BOTÓN DE AYUDA Y SOPORTE ACTUALIZADO */}
                  <a 
                    href="https://wa.me/51955000321?text=Hola,%20deseo%20soporte%20con%20la%20plataforma%20de%20firma%20de%20Inkless."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors rounded-lg"
                  >
                    <HelpCircle className="w-4 h-4 text-slate-500" />
                    Ayuda y Soporte
                  </a>


                </div>

                {/* Divider y Logout */}
                <div className="border-t border-slate-100 py-2">
                  <button
                    onClick={onLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Cerrar Sesión
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Búsqueda móvil (visible solo en móvil cuando está enfocada) */}
      {searchFocused && (
        <div className="md:hidden border-t border-slate-200 px-4 py-3 bg-slate-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar..."
              className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              autoFocus
            />
          </div>
        </div>
      )}
    </header>
  );
}